/**
 * vendor_widget_controller.js — base class for Stimulus controllers that wrap a
 * DOM-rewriting vendor widget (Choices.js, Radar AddressAutocomplete, ...).
 *
 * THE RULE THIS CLASS ENCODES: bind the controller to a STABLE CONTAINER and let
 * the vendor rewrite a CHILD of that container. Never bind a controller directly
 * to the element the vendor reparents.
 *
 * Why: Stimulus pairs connect()/disconnect() off a single MutationObserver on the
 * persistent document. A vendor that MOVES its host element (Choices moves the
 * <select> into a .choices wrapper; AddressAutocomplete.createDropdown moves the
 * <input> into a generated wrapper) is observed as the controller's own element
 * leaving its position -> Stimulus fires disconnect, then connect on a FRESH
 * instance. If connect builds and disconnect tears down, that is an infinite
 * build/move/disconnect/reconnect loop that hard-freezes the tab. Instance-level
 * guards cannot stop it (each reconnect is a new instance), which is why the old
 * select/autocomplete controllers needed element-marker + isConnected hacks.
 *
 * Binding to a container removes the loop STRUCTURALLY: the vendor only reparents
 * a descendant, which stays inside the controller's subtree, so the controller's
 * own element never moves and disconnect() fires only on a REAL removal (Turbo
 * body swap, Turbo Stream re-render). No guard code is needed — and there is none
 * here. site_picker_controller.js is the in-repo precedent (it wraps BOTH Choices
 * and Radar this way with zero loop defense). tests/unit/test_stimulus_migration.py
 * enforces that no controller binds directly to a reparented <select>/<input> leaf.
 *
 * Subclasses override the hooks at the bottom. The lifecycle (lazy-vendor wait,
 * build, before-cache teardown, disconnect teardown) is shared here.
 *
 * WAITING FOR A LAZY VENDOR: the global (Choices, Radar, ...) is fetched by
 * PageLoader on turbo:load, which fires AFTER the body swap that connected this
 * controller. A bounded poll alone therefore races the fetch and loses whenever
 * the network is slow or the request fails and only succeeds on a later
 * turbo:load — and once it gives up, the element stays un-enhanced forever even
 * though the vendor is now on window. So we do both: poll for the already-loaded
 * fast path, and listen for PageLoader's `pageloader:module-loaded` until built.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            this._retries = this.retryCount();
            // PageLoader fetches the vendor on turbo:load, which fires AFTER the
            // body swap that connected this controller — so on every Turbo
            // navigation the poll below starts before the request is even issued,
            // and a slow or failed-then-retried fetch outruns it. Listening for
            // the load is what makes that unlosable: the poll is a fast path for
            // the already-loaded case, this is the guarantee. Kept until built,
            // so a fetch that only succeeds on a later turbo:load still lands.
            this._onVendorAsset = () => this._tryInit();
            document.addEventListener('pageloader:module-loaded', this._onVendorAsset);
            this._tryInit();
        }

        _tryInit() {
            // Guard on a flag, not on this.instance — buildWidget() may legitimately
            // return null, and a null instance must not read as "not built yet" or
            // every subsequent module-loaded event would rebuild the widget.
            if (this._built) return;
            if (this.vendorReady()) {
                this._build();
                return;
            }
            if (this._retries > 0) {
                this._retries -= 1;
                this._retryTimer = setTimeout(() => this._tryInit(), this.retryMs());
            }
        }

        _build() {
            this._retryTimer = null;
            var el = this.widgetElement();
            if (!el) return;
            this.widgetEl = el;
            this._built = true;
            this.instance = this.buildWidget(el);
            this._stopWaitingForVendor();
            this._onBeforeCache = () => this._teardown();
            document.addEventListener('turbo:before-cache', this._onBeforeCache);
        }

        _stopWaitingForVendor() {
            if (this._onVendorAsset) {
                document.removeEventListener('pageloader:module-loaded', this._onVendorAsset);
                this._onVendorAsset = null;
            }
        }

        disconnect() {
            if (this._retryTimer) {
                clearTimeout(this._retryTimer);
                this._retryTimer = null;
            }
            this._stopWaitingForVendor();
            if (this._onBeforeCache) {
                document.removeEventListener('turbo:before-cache', this._onBeforeCache);
            }
            this._teardown();
        }

        _teardown() {
            if (this.instance) {
                try {
                    this.destroyWidget(this.instance, this.widgetEl);
                } catch (_e) {}
                this.instance = null;
            }
        }

        // ---- subclass hooks -------------------------------------------------
        // How many times to poll for the lazy-loaded vendor global before giving
        // up (RETRY_MS apart).
        retryCount() { return 10; }
        retryMs() { return 100; }
        // True once the vendor global is available (e.g. typeof Choices !== 'undefined').
        vendorReady() { return true; }
        // The CHILD element the vendor rewrites, found within this.element (the
        // stable container). Returns null when absent -> _build() is a no-op.
        widgetElement() { return null; }
        // Construct the widget on `el`; return the instance (parked by _build()).
        buildWidget(_el) { return null; }
        // Reverse buildWidget(): destroy the instance and drop any element marker.
        destroyWidget(_instance, _el) {}
}
